import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  provedorDeIdentidade as provedorPadrao,
  type Credenciais,
  type DesafioDeAcesso,
  type ProvedorDeIdentidade,
  type Sessao,
} from './provedorDeIdentidade';

interface ValorDaIdentidade {
  sessao: Sessao | null;
  autenticado: boolean;
  /**
   * Ainda perguntando ao servidor quem está logado.
   *
   * Existe porque o cookie é `HttpOnly` e a resposta não é síncrona. Sem
   * este estado, o primeiro render de quem tem sessão válida veria
   * `autenticado: false` e a guarda de rota mandaria a pessoa para o login
   * — um redirecionamento visível a cada F5.
   */
  carregando: boolean;
  solicitarCodigo: (usuario: string) => Promise<DesafioDeAcesso>;
  autenticar: (credenciais: Credenciais) => Promise<Sessao>;
  encerrar: () => Promise<void>;
}

const ContextoDeIdentidade = createContext<ValorDaIdentidade | null>(null);

/**
 * O contexto é fino de propósito: ele só REPASSA o provedor. Toda a lógica
 * de identidade mora atrás da porta única, e é por isso que trocar o modelo
 * real não obriga a mexer em componente nenhum.
 */
export function ProvedorDeIdentidadeReact({
  children,
  provedor = provedorPadrao,
}: {
  children: React.ReactNode;
  provedor?: ProvedorDeIdentidade;
}): React.JSX.Element {
  const [sessao, definirSessao] = useState<Sessao | null>(() => provedor.obterSessao());
  const [carregando, definirCarregando] = useState(() => provedor.obterSessao() === null);
  const clienteDeQuery = useQueryClient();

  // Sessão zerada por QUALQUER caminho (logout, ociosidade, 401 no meio do uso)
  // leva o cache junto: dado de paciente não fica na memória da aba sem sessão.
  useEffect(
    () =>
      provedor.aoMudar((nova) => {
        definirSessao(nova);
        if (nova === null) clienteDeQuery.clear();
      }),
    [provedor, clienteDeQuery],
  );

  useEffect(() => {
    let vivo = true;
    void provedor.carregar().finally(() => {
      // `vivo` evita atualizar estado depois do unmount — que em StrictMode
      // acontece a cada montagem dupla do desenvolvimento.
      if (vivo) definirCarregando(false);
    });
    return () => {
      vivo = false;
    };
  }, [provedor]);

  // `provedorDeIdentidade.ts` continua sendo a ÚNICA porta de SESSÃO — mas
  // o cache do React Query não é sessão, é um efeito colateral da troca de
  // identidade, e este componente já é onde as duas se encontram (é ele que
  // provê `useQueryClient`). Sem limpar aqui, trocar de usuário na mesma aba
  // (núcleo A → núcleo B) deixaria a fila/detalhe do núcleo A visível no
  // cache por até `staleTime`/`gcTime` — achado do `security-auditor`
  // (`T15`): `T11`/`T13` são a primeira feature a colocar PII de paciente
  // no `QueryClient`.
  const autenticar = useCallback(
    async (credenciais: Credenciais) => {
      const novaSessao = await provedor.autenticar(credenciais);
      clienteDeQuery.clear();
      return novaSessao;
    },
    [provedor, clienteDeQuery],
  );

  // Primeira etapa do login: não mexe em sessão nem em cache — só repassa.
  const solicitarCodigo = useCallback((usuario: string) => provedor.solicitarCodigo(usuario), [provedor]);

  const encerrar = useCallback(async () => {
    try {
      await provedor.encerrar();
    } finally {
      // Mesmo se o DELETE falhar: o provedor já zerou a sessão local, e o cache
      // com dado de paciente não pode sobreviver a quem clicou em sair.
      clienteDeQuery.clear();
    }
  }, [provedor, clienteDeQuery]);

  const valor = useMemo<ValorDaIdentidade>(
    () => ({ sessao, autenticado: sessao !== null, carregando, solicitarCodigo, autenticar, encerrar }),
    [sessao, carregando, solicitarCodigo, autenticar, encerrar],
  );

  return <ContextoDeIdentidade.Provider value={valor}>{children}</ContextoDeIdentidade.Provider>;
}

// O provedor e o hook vivem juntos de propósito: separá-los criaria um
// arquivo só para o `createContext`, e a porta única da identidade ficaria
// espalhada em três arquivos em vez de um contexto fino e um provedor.
// eslint-disable-next-line react-refresh/only-export-components
export function useIdentidade(): ValorDaIdentidade {
  const valor = useContext(ContextoDeIdentidade);
  if (valor === null) {
    throw new Error('useIdentidade precisa estar dentro de <ProvedorDeIdentidadeReact>.');
  }
  return valor;
}
